import { Purchases, PurchasesPackage, CustomerInfo } from '@revenuecat/purchases-capacitor';
import { Capacitor } from '@capacitor/core';
import { supabase } from '@/integrations/supabase/client';
import { SupabaseClient } from '@supabase/supabase-js';
import { Database } from '@/types/supabase';

// Define the Subscription data interface to match the database structure
interface SubscriptionData {
  user_id: string;
  plan: string;
  source: string | null;
  ends_at: string | null;
  grace_until: string | null;
  current_time: string | null;
  active_project_count: number;
  passive_project_count: number;
  active_project_limit: number;
  projects_locked: boolean;
  trial_active: boolean;
  has_access: boolean;
  subscription_status: 'trial' | 'active' | 'expired';
}

// Create a simpler subset type for CustomerInfo to avoid deep type issues
type BasicCustomerInfo = {
  entitlements: {
    active: Record<string, any>;
    verification?: string;
    all?: Record<string, any>;
  };
  activeSubscriptions: string[];
  allPurchasedProductIdentifiers: string[];
  firstSeen: string;
  originalAppUserId: string;
  managementURL: string | null;
  latestExpirationDate: string | null;
  nonSubscriptionTransactions: any[];
  originalPurchaseDate: string | null;
  requestDate: string;
  requestDateMillis: number;
};

// Product IDs - Platform specific mappings
// iOS uses App Store Connect identifiers
export const IOS_PREMIUM_MONTHLY = 'com.santiyex.premium_monthly';
export const IOS_ELITE_MONTHLY = 'com.santiyex.elite_monthly';

// Android uses Google Play Console identifiers
export const ANDROID_PREMIUM_MONTHLY = 'premiumsantiyex:premiumsantiyex';
export const ANDROID_ELITE_MONTHLY = 'elitesantiyex:elitesantiyex';

// Legacy exports for backward compatibility
export const PREMIUM_MONTHLY = IOS_PREMIUM_MONTHLY;
export const ELITE_MONTHLY = IOS_ELITE_MONTHLY;

// Trial period in days
export const TRIAL_PERIOD_DAYS = 90;

/**
 * PurchaseService handles in-app purchase functionality via RevenueCat
 * This is implemented as a singleton to ensure login state persists
 */
class PurchaseService {
  private initialized = false;
  private initializing = false;
  private initPromise: Promise<void> | null = null;
  private initializationError: Error | null = null;
  private isRcLoggedIn = false;
  private rcLoginUserID: string | null = null;
  private rcLoginInProgress = false;
  private lastCustomerInfoFetch = 0;
  private customerInfoCache: BasicCustomerInfo | null = null;
  private readonly CUSTOMER_INFO_CACHE_DURATION = 30000; // 30 seconds
  
  // Use private constructor for singleton pattern
  private static instance: PurchaseService | null = null;
  
  private constructor() {
    // Nothing special in constructor
  }
  
  // Static method to get the singleton instance
  public static getInstance(): PurchaseService {
    if (!PurchaseService.instance) {
      PurchaseService.instance = new PurchaseService();
    }
    return PurchaseService.instance;
  }

  // Static method to access forceLoginFix
  public static async forceLoginFix(): Promise<boolean> {
    return await PurchaseService.getInstance().forceLoginFix();
  }

  public static async debugPackages(): Promise<void> {
    return await PurchaseService.debugPackageIdentifiers();
  }

  // Function to check if running on a mobile platform
  private isPlatform(platform: string): boolean {
    return Capacitor.getPlatform() === platform;
  }

  // Get platform-specific product identifiers
  private getPlatformProductIdentifiers(): { premium: string; elite: string } {
    if (this.isPlatform('android')) {
      return {
        premium: ANDROID_PREMIUM_MONTHLY,
        elite: ANDROID_ELITE_MONTHLY
      };
    } else {
      // Default to iOS identifiers for iOS and web
      return {
        premium: IOS_PREMIUM_MONTHLY,
        elite: IOS_ELITE_MONTHLY
      };
    }
  }

  // Helper to map generic plan types to platform-specific identifiers
  private mapPlanToProductId(planType: 'premium' | 'elite'): string {
    const identifiers = this.getPlatformProductIdentifiers();
    return identifiers[planType];
  }

  // Debug function to analyze package-identifier mismatches
  static async debugPackageIdentifiers(): Promise<void> {
    try {
      const instance = PurchaseService.getInstance();
      const platform = Capacitor.getPlatform();
      const expectedIdentifiers = instance.getPlatformProductIdentifiers();
      
      console.log(`🐛 [DEBUG] Platform: ${platform}`);
      console.log(`🐛 [DEBUG] Expected identifiers:`, expectedIdentifiers);
      
      const offerings = await instance.getOfferings();
      
      if (offerings && 'all' in offerings && offerings.all) {
        console.log(`🐛 [DEBUG] Available offerings:`);
        Object.entries(offerings.all).forEach(([key, offering]) => {
          console.log(`  📋 Offering "${key}":`, {
            identifier: offering.identifier,
            packageCount: offering.availablePackages?.length || 0
          });
          
          if (offering.availablePackages) {
            offering.availablePackages.forEach((pkg, index) => {
              const matchesPremium = pkg.identifier === expectedIdentifiers.premium || 
                                   pkg.product?.identifier === expectedIdentifiers.premium;
              const matchesElite = pkg.identifier === expectedIdentifiers.elite || 
                                 pkg.product?.identifier === expectedIdentifiers.elite;
              
              console.log(`    📦 Package ${index + 1}:`, {
                identifier: pkg.identifier,
                productId: pkg.product?.identifier,
                title: pkg.product?.title,
                matches: {
                  premium: matchesPremium,
                  elite: matchesElite
                }
              });
            });
          }
        });
      } else {
        console.log(`🐛 [DEBUG] No offerings available`);
      }
    } catch (error) {
      console.error(`🐛 [DEBUG] Error analyzing packages:`, error);
    }
  }

  // Check if the current platform is a mobile platform (iOS or Android)
  isMobilePlatform(): boolean {
    return this.isPlatform('ios') || this.isPlatform('android');
  }

  // Log errors without crashing
  private logError(message: string, error: any) {
    
  }

  // Centralized method to login to RevenueCat with Supabase user ID
  async ensureRcLogin(): Promise<boolean> {
    // Skip if not on mobile platform
    if (!this.isMobilePlatform()) {
      
      return false;
    }

    try {
      // Set login in progress flag
      this.rcLoginInProgress = true;
      
      
      // Get current Supabase user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError || !user) {
        
        this.rcLoginInProgress = false;
        return false;
      }

      

      // Check if already logged in with this user ID
      if (this.isRcLoggedIn && this.rcLoginUserID === user.id && this.initialized) {
        
        this.rcLoginInProgress = false;
        return true;
      }

      // If RevenueCat is not yet initialized, initialize it with the user ID
      if (!this.initialized) {
        
        await this.initialize(user.id);
        
        // If initialization was successful, we're already logged in
        if (this.initialized && this.isRcLoggedIn && this.rcLoginUserID === user.id) {
          
          this.rcLoginInProgress = false;
          return true;
        }
      }

      // If we reach here, RevenueCat was already initialized but possibly with a different user
      // This should rarely happen with our new approach, but we handle it for safety
      
      try {
        const { customerInfo } = await Purchases.getCustomerInfo();
        const revenueCatUserId = customerInfo.originalAppUserId;
        
        
        
        if (revenueCatUserId === user.id) {
          
          this.isRcLoggedIn = true;
          this.rcLoginUserID = user.id;
          this.rcLoginInProgress = false;
          return true;
        }
        
        // If there's a mismatch, we have a problem because we should never be in this state
        // with our new approach. Log it but don't attempt login to avoid creating issues.
        
        
        
        
        // For safety, mark as not logged in and return false
        this.isRcLoggedIn = false;
        this.rcLoginUserID = null;
        this.rcLoginInProgress = false;
        return false;
        
      } catch (infoError) {
        
        this.rcLoginInProgress = false;
        return false;
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      const errorStack = error instanceof Error ? error.stack : '';
      
      
      
      this.isRcLoggedIn = false;
      this.rcLoginUserID = null;
      this.rcLoginInProgress = false;
      return false;
    }
  }

  // Centralized guard to verify user is logged in to RevenueCat
  async throwIfNotLoggedIn(): Promise<void> {
    // Always ensure login first
    const loggedIn = await this.ensureRcLogin();
    
    if (!loggedIn) {
      
      throw new Error('RevenueCat user not logged in. Please authenticate first.');
    }
  }
  
  // Check if RevenueCat login is in progress
  isLoginInProgress(): boolean {
    const result = this.rcLoginInProgress;
    
    return result;
  }
  
  // Check if user is logged in to RevenueCat
  isUserLoggedIn(): boolean {
    const isLoggedIn = this.isRcLoggedIn && !!this.rcLoginUserID;
    
    return isLoggedIn;
  }

  // Clear customer info cache (useful after purchases, etc.)
  clearCustomerInfoCache(): void {
    this.customerInfoCache = null;
    this.lastCustomerInfoFetch = 0;
    
  }

  async initialize(appUserID?: string) {
    // If already initialized, return immediately
    if (this.initialized) {
      
      return;
    }
    
    // If initialization is in progress, wait for it
    if (this.initializing) {
      
      return this.initPromise;
    }
    
    this.initializing = true;
    
    
    this.initPromise = new Promise<void>(async (resolve) => {
      try {
        if (!this.isPlatform('ios') && !this.isPlatform('android')) {
          
          this.initialized = true;
          this.initializing = false;
          resolve();
          return;
        }
        
        // CRITICAL: Only configure RevenueCat if we have a Supabase user ID
        // This prevents anonymous ID creation as per RevenueCat documentation
        if (!appUserID) {
          
          
          this.initialized = false; // Keep as not initialized until we have a user ID
          this.initializing = false;
          resolve();
          return;
        }
        
        try {
          // Get RevenueCat API key from environment
          const apiKey = this.isPlatform('ios') 
            ? import.meta.env.VITE_REVENUECAT_IOS_KEY 
            : import.meta.env.VITE_REVENUECAT_ANDROID_KEY;
            
          if (!apiKey) {
            throw new Error('RevenueCat API key is missing');
          }
          
          
          
          
          // Set timeout for initialization to prevent hanging
          const initPromise = Purchases.configure({
            apiKey,
            appUserID // ALWAYS provide the Supabase user ID to prevent anonymous ID creation
          });
          
          const timeoutPromise = new Promise<void>((_, reject) => {
            setTimeout(() => reject(new Error('RevenueCat initialization timeout')), 5000);
          });
          
          await Promise.race([initPromise, timeoutPromise]);
          
          
          // Mark user as logged in since we configured with their ID
          this.isRcLoggedIn = true;
          this.rcLoginUserID = appUserID;
          
          this.initialized = true;
          
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Unknown error';
          
          
          this.initializationError = error instanceof Error ? error : new Error('Unknown initialization error');
          
          // Still mark as initialized so app can function without RevenueCat
          this.initialized = true;
          
        }
        
        resolve();
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        
        
        this.initialized = true; // Mark as initialized anyway to avoid blocking the app
        resolve();
      } finally {
        this.initializing = false;
      }
    });
    
    return this.initPromise;
  }

  async getTrialDaysRemaining(): Promise<number> {
    try {
      // Get the current user's data from Supabase Auth
      const { data: { user }, error: userError } = await supabase.auth.getUser();

      if (userError || !user) {
        // Default to 0 days remaining if user data is unavailable
        return 0;
      }

      // Get subscription data from the subscriptions table
      const { data, error } = await supabase
        .from('subscription_status_view')
        .select('trial_active, subscription_status, ends_at')
        .eq('user_id', user.id) // Changed from 'id' to 'user_id'
        .single() as { data: SubscriptionData | null, error: any };
      
      if (error || !data) {
        
        return 0;
      }

      // If trial is not active or subscription status is not 'trial', return 0
      if (!data.trial_active || data.subscription_status !== 'trial') {
        return 0;
      }

      // Calculate days remaining
      const now = new Date();
      const trialEnd = new Date(data.ends_at || '');
      
      // If trial already ended, return 0
      if (now > trialEnd) {
        return 0;
      }
      
      // Calculate the remaining days
      const diffInMs = trialEnd.getTime() - now.getTime();
      const diffInDays = Math.ceil(diffInMs / (1000 * 60 * 60 * 24));
      
      return diffInDays > 0 ? diffInDays : 0;
    } catch (error) {
      this.logError('Error calculating trial days remaining', error);
      return 0;
    }
  }

  async isTrialExpired(): Promise<boolean> {
    try {
      // Get the current user's data from Supabase Auth
      const { data: { user }, error: userError } = await supabase.auth.getUser();

      if (userError || !user) {
        // Default to expired if we can't get user data
        return true;
      }

      // Get subscription data from the subscriptions table
      const { data, error } = await supabase
        .from('subscription_status_view')
        .select('trial_active, subscription_status, projects_locked, ends_at, has_access')
        .eq('user_id', user.id) // Changed from 'id' to 'user_id'
        .single() as { data: SubscriptionData | null, error: any };
      
      if (error || !data) {
        
        return true;
      }
      
      // Directly use has_access from the view if available
      if (data.has_access !== undefined) {
        return !data.has_access;
      }
      
      // If projects are locked, the subscription is considered expired
      if (data.projects_locked) {
        return true;
      }

      // Get current date for comparison
      const now = new Date();
      
      // Check based on subscription status
      if (data.subscription_status === 'trial') {
        // Check if trial is active
        if (!data.trial_active) {
          return true;
        }
        
        // Check if trial end date is in the past
        const trialEnd = new Date(data.ends_at || '');
        return now > trialEnd;
      } 
      else if (data.subscription_status === 'active') {
        // Check if there's an expiration date for active subscriptions
        if (data.ends_at) {
          const expirationDate = new Date(data.ends_at);
          return now > expirationDate;
        }
        
        // Active subscription with no expiration date is considered valid
        return false;
      }
      else if (data.subscription_status === 'expired') {
        // Explicitly marked as expired
        return true;
      }
      
      // Default to expired for any other status
      return true;
    } catch (error) {
      this.logError('Error checking if subscription is expired', error);
      return true; // Default to expired on error
    }
  }

  async getOfferings() {
    try {
      const platform = Capacitor.getPlatform();
      const expectedIdentifiers = this.getPlatformProductIdentifiers();
      
      console.log(`📦 [${platform}] Fetching offerings with expected identifiers:`, expectedIdentifiers);
      
      // Remove standalone initialize() - ensureRcLogin() will handle initialization with user ID
      
      if (this.isMobilePlatform()) {
        // For mobile platforms, ensure user is logged in first
        
        
        // CRITICAL: Ensure user is logged in before fetching offerings
        // This will also initialize RevenueCat with the user ID if needed
        const loggedIn = await this.ensureRcLogin();
        if (!loggedIn) {
          console.warn(`⚠️ [${platform}] Failed to login to RevenueCat`);
        }
        
        const offerings = await Purchases.getOfferings();
        
        console.log(`📦 [${platform}] Retrieved offerings:`, {
          hasOfferings: !!offerings,
          hasCurrent: !!offerings?.current,
          currentPackageCount: offerings?.current?.availablePackages?.length || 0,
          allOfferingsCount: offerings?.all ? Object.keys(offerings.all).length : 0
        });
        
        if (!offerings || !offerings.current || !offerings.current.availablePackages || offerings.current.availablePackages.length === 0) {
          
          return { current: null, all: {} };
        }
        
        // Log all available offerings for debugging
        if (offerings.all) {
          console.log(`🔍 [${platform}] Found ${Object.keys(offerings.all).length} offerings`);
          
          Object.entries(offerings.all).forEach(([key, offering]) => {
            console.log(`📦 [${platform}] Offering "${key}":`, {
              identifier: offering.identifier,
              packageCount: offering.availablePackages?.length || 0
            });
            
            if (offering.availablePackages) {
              offering.availablePackages.forEach((pkg: any) => {
                console.log(`  📋 [${platform}] Package:`, {
                  identifier: pkg.identifier,
                  productId: pkg.product?.identifier,
                  title: pkg.product?.title,
                  price: pkg.product?.price
                });
              });
            }
          });
        }
        
        if (!offerings.current) {
          
        } else if (!offerings.current.availablePackages || offerings.current.availablePackages.length === 0) {
          
        }
        
        return offerings;
      } else {
        // For web/desktop, return dummy offerings
        
        const dummyPackages = await this.getDummyPackages();
        return {
          current: {
            identifier: 'default',
            availablePackages: dummyPackages
          }
        };
      }
    } catch (error) {
      this.logError('Error getting offerings', error);
      
      // On mobile, don't fall back to dummy offerings
      if (this.isMobilePlatform()) {
        throw error;
      }
      
      // For web/desktop, fallback to dummy offerings if RevenueCat fails
      
      const dummyPackages = await this.getDummyPackages();
      return {
        current: {
          identifier: 'default',
          availablePackages: dummyPackages
        }
      };
    }
  }

  // Get dummy package objects for development or when RevenueCat fails
  async getDummyPackages(): Promise<PurchasesPackage[]> {
    const identifiers = this.getPlatformProductIdentifiers();
    
    return [
      {
        identifier: 'premium_monthly',
        packageType: 'MONTHLY',
        product: {
          identifier: identifiers.premium,
          title: 'Premium Plan',
          description: '2 Aktif Proje ve Temel Destek',
          price: 749,
          priceString: '749 ₺'
        }
      },
      {
        identifier: 'elite_monthly',
        packageType: 'MONTHLY',
        product: {
          identifier: identifiers.elite,
          title: 'Elite Plan',
          description: '5 Aktif Proje ve Öncelikli Destek',
          price: 999,
          priceString: '999 ₺'
        }
      }
    ] as PurchasesPackage[];
  }

  async purchasePackage(pkg: PurchasesPackage) {
    // Remove standalone initialize() - ensureRcLogin() will handle initialization with user ID
    
    try {
      const productId = pkg.product.identifier;
      const platform = Capacitor.getPlatform();
      const identifiers = this.getPlatformProductIdentifiers();
      
      console.log(`🛒 [${platform}] Starting purchase for product:`, {
        productId,
        packageIdentifier: pkg.identifier,
        expectedIdentifiers: identifiers,
        productTitle: pkg.product.title
      });
      
      // Fail early if not logged in - this will also initialize RevenueCat if needed
      await this.throwIfNotLoggedIn();

      // Now we can safely proceed with the purchase
      const result = await Purchases.purchasePackage({
        aPackage: pkg
      });
      
      
      
      // After successful purchase, update subscription in database.
      // 2-tier model: any paid SKU → plan='premium', tier='premium'.
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const { error } = await supabase
            .from('subscriptions')
            .update({
              plan: 'premium',
              tier: 'premium',
              source: 'revenuecat',
              last_activity: 'PURCHASE',
              last_activity_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
            })
            .eq('user_id', user.id);
            
          if (error) {
            
          } else {
            
          }
            
          // Force refresh from RevenueCat and then update database
          await this.refreshOfferings();
          await this.refreshSubscription();
        }
      } catch (dbError) {
        this.logError('Error updating subscription in database', dbError);
        // Continue even if database update fails
      }
      
      return result;
    } catch (error: any) {
      // Purchase error handling
      if (error.code === 'PURCHASE_CANCELLED_ERROR') {
        // This is a user cancellation, not a real error
        
        const cancelError = new Error('User cancelled the purchase');
        throw cancelError;
      } else {
        // Log detailed error information
        
        
        this.logError('Error making purchase', error);
        throw error;
      }
    }
  }

  async getCustomerInfo(): Promise<BasicCustomerInfo> {
    // Check cache first to avoid excessive API calls
    const now = Date.now();
    if (this.customerInfoCache && (now - this.lastCustomerInfoFetch) < this.CUSTOMER_INFO_CACHE_DURATION) {
      
      return this.customerInfoCache;
    }

    try {
      // Remove standalone initialize() - ensureRcLogin() will handle initialization with user ID
      
      if (!this.isPlatform('ios') && !this.isPlatform('android')) {
        try {
          // Get the current user's data
          const { data: { user }, error: userError } = await supabase.auth.getUser();
          if (userError || !user) {
            throw new Error('User not authenticated');
          }
          
          // Get subscription data from the subscriptions table
          const { data, error } = await supabase
            .from('subscriptions')
            .select('*')
            .eq('user_id', user.id)
            .single();
          
          if (error || !data) {
            
            throw error;
          }
          
          // Determine active entitlements based on subscription status
          const entitlements: Record<string, any> = {};
          if (data.subscription_status === 'active') {
            if (data.plan === 'premium' || data.plan === 'Premium') {
              entitlements.premium = {
                identifier: 'premium',
                isActive: true,
                willRenew: true
              };
            } else if (data.plan === 'elite' || data.plan === 'Elite') {
              entitlements.elite = {
                identifier: 'elite',
                isActive: true,
                willRenew: true
              };
            }
          }
          
          // Return formatted customer info with our simpler type
          const customerInfo = {
            entitlements: {
              active: entitlements
            },
            activeSubscriptions: Object.keys(entitlements).length > 0 ? [data.plan.toLowerCase()] : [],
            allPurchasedProductIdentifiers: Object.keys(entitlements).length > 0 ? [data.plan.toLowerCase()] : [],
            firstSeen: data.created_at,
            originalAppUserId: user.id,
            managementURL: null,
            latestExpirationDate: data.expires_at,
            nonSubscriptionTransactions: [],
            originalPurchaseDate: data.initial_subscription_at,
            requestDate: new Date().toISOString(),
            requestDateMillis: Date.now(),
          };
          
          // Cache the result
          this.customerInfoCache = customerInfo;
          this.lastCustomerInfoFetch = now;
          return customerInfo;
        } catch (error) {
          this.logError('Error getting simulated customer info', error);
          
          // Return a safe default
          const defaultInfo = {
            entitlements: {
              active: {}
            },
            activeSubscriptions: [],
            allPurchasedProductIdentifiers: [],
            firstSeen: new Date().toISOString(),
            originalAppUserId: '',
            managementURL: null,
            latestExpirationDate: null,
            nonSubscriptionTransactions: [],
            originalPurchaseDate: null,
            requestDate: new Date().toISOString(),
            requestDateMillis: Date.now(),
          };
          
          // Cache the result
          this.customerInfoCache = defaultInfo;
          this.lastCustomerInfoFetch = now;
          return defaultInfo;
        }
      }
      
      // Ensure user is logged in to RevenueCat before getting customer info
      await this.ensureRcLogin();
      
      // Set a timeout for the RevenueCat API call to prevent hanging
      try {
        const customerInfoPromise = Purchases.getCustomerInfo();
        const timeoutPromise = new Promise<never>((_, reject) => 
          setTimeout(() => reject(new Error('getCustomerInfo timeout')), 3000)
        );
        
        const result = await Promise.race([customerInfoPromise, timeoutPromise]);
        
        // After getting info from RevenueCat, sync with our database
        try {
          const { data: { user } } = await supabase.auth.getUser();
          if (user) {
            const entitlements = result.customerInfo.entitlements.active;
            let plan = '';
            let projectLimit = 1;
            let subscriptionStatus = 'expired';
            
            if (Object.keys(entitlements).length > 0) {
              subscriptionStatus = 'active';
              // 2-tier model: any paid entitlement → plan='premium', tier='premium'.
              plan = 'premium';
              projectLimit = 5;
            }

            if (plan && subscriptionStatus === 'active') {
              // Only columns that actually exist on public.subscriptions.
              await supabase
                .from('subscriptions')
                .update({
                  plan,
                  tier: 'premium',
                  last_activity: 'SYNC',
                  last_activity_at: new Date().toISOString(),
                  updated_at: new Date().toISOString(),
                })
                .eq('user_id', user.id);
            }
          }
        } catch (dbError) {
          this.logError('Error syncing subscription to database', dbError);
          // Continue even if sync fails
        }
        
        return result.customerInfo as unknown as BasicCustomerInfo;
      } catch (error) {
        this.logError('Error getting customer info', error);
        
        // Return a safe default
        return {
          entitlements: {
            active: {}
          },
          activeSubscriptions: [],
          allPurchasedProductIdentifiers: [],
          firstSeen: new Date().toISOString(),
          originalAppUserId: '',
          managementURL: null,
          latestExpirationDate: null,
          nonSubscriptionTransactions: [],
          originalPurchaseDate: null,
          requestDate: new Date().toISOString(),
          requestDateMillis: Date.now(),
        };
      }
    } catch (error) {
      this.logError('Unexpected error in getCustomerInfo', error);
      
      // Return a safe default
      return {
        entitlements: {
          active: {},
          verification: "VERIFIED",
          all: {}
        },
        activeSubscriptions: [],
        allPurchasedProductIdentifiers: [],
        firstSeen: new Date().toISOString(),
        originalAppUserId: '',
        requestDate: new Date().toISOString(),
        requestDateMillis: Date.now(),
        latestExpirationDate: null,
        nonSubscriptionTransactions: [],
        originalPurchaseDate: null,
        managementURL: null,
      };
    }
  }

  async restorePurchases(): Promise<BasicCustomerInfo> {
    // Remove standalone initialize() - ensureRcLogin() will handle initialization with user ID
    
    if (!this.isPlatform('ios') && !this.isPlatform('android')) {
      // In web/desktop development, just return current subscription info
      return await this.getCustomerInfo();
    }
    
    try {
      // Ensure user is logged in to RevenueCat before restoring purchases
      const loggedIn = await this.ensureRcLogin();
      if (!loggedIn) {
        throw new Error('Cannot restore purchases without authenticating with Supabase user ID');
      }
      
      const { customerInfo } = await Purchases.restorePurchases();
      
      // Sync restored purchases with our database
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const entitlements = customerInfo.entitlements.active;
          let plan = '';
          let projectLimit = 1;
          let subscriptionStatus = 'expired';
          
          if (Object.keys(entitlements).length > 0) {
            subscriptionStatus = 'active';
            // 2-tier model: any paid entitlement → plan='premium', tier='premium'.
            plan = 'premium';
            projectLimit = 5;
          }

          if (plan && subscriptionStatus === 'active') {
            // Only columns that actually exist on public.subscriptions.
            await supabase
              .from('subscriptions')
              .update({
                plan,
                tier: 'premium',
                last_activity: 'RESTORE',
                last_activity_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
              })
              .eq('user_id', user.id);
          }
        }
      } catch (dbError) {
        this.logError('Error syncing restored purchases to database', dbError);
        // Continue even if sync fails
      }
      
      return customerInfo as unknown as BasicCustomerInfo;
    } catch (error) {
      this.logError('Error restoring purchases', error);
      throw error;
    }
  }
  
  // Method to refresh the trial status
  async refreshTrialStatus(): Promise<number> {
    try {
      // Get current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        return 0;
      }
      
      // Get trial data from the subscriptions table
      const { data, error } = await supabase
        .from('subscription_status_view')
        .select('trial_active, ends_at')
        .eq('user_id', user.id)
        .single();
      
      if (error) {
        
        return 0;
      }
      
      // Return the updated trial days remaining
      return this.getTrialDaysRemaining();
    } catch (error) {
      this.logError('Error refreshing trial status', error);
      return 0;
    }
  }

  // Force a fresh fetch of RevenueCat offerings - useful for debugging
  async refreshOfferings() {
    if (!this.isMobilePlatform()) {
      
      return await this.getOfferings();
    }
    
    try {
      
      
      // CRITICAL: Ensure user is logged in before refreshing offerings
      const loggedIn = await this.ensureRcLogin();
      if (!loggedIn) {
        
      }
      
      await Purchases.syncPurchases();
      const offerings = await Purchases.getOfferings(); // true = force refresh
      
      
      
      // Log all available offerings and packages
      if (offerings.all) {
        
        
        Object.entries(offerings.all).forEach(([key, offering]) => {
          
          if (offering.availablePackages) {
            
          }
        });
      }
      
      return offerings;
    } catch (error) {
      this.logError('Error refreshing offerings', error);
      throw error;
    }
  }

  // Force refresh subscription status
  async refreshSubscription() {
    try {
      // Get current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      // Get customer info from RevenueCat
      const customerInfo = await this.getCustomerInfo();
      const entitlements = customerInfo.entitlements.active;
      const hasActiveSubscription = Object.keys(entitlements).length > 0;

      

      // Check current subscription in database for sync validation
      type SubscriptionResponse = {
        user_id: string;
        plan: string;
        source: string;
        ends_at: string | null;
        grace_until: string | null;
        active_project_count: number;
        passive_project_count: number;
        last_activity: string;
        last_activity_at: string;
      };

      // Get current subscription from database (read-only)
      const { data: subscription } = await (supabase as SupabaseClient)
        .from('subscriptions')
        .select('*')
        .eq('user_id', user.id)
        .single() as { data: SubscriptionResponse | null };

      

      // Validate sync status (but don't update database - webhooks handle that)
      if (hasActiveSubscription) {
        let plan = 'premium'; // default
        if (entitlements.elite) {
          plan = 'elite';
        } else if (entitlements.premium) {
          plan = 'premium';
        }
        
        
        
        // Log sync status for monitoring
        if (subscription && subscription.source === 'revenuecat') {
          const dbActive = subscription.ends_at && new Date(subscription.ends_at) > new Date();
          
        }
      } else {
        
        
        // Log sync status for monitoring
        if (subscription && subscription.source === 'revenuecat') {
          const dbActive = subscription.ends_at && new Date(subscription.ends_at) > new Date();
          if (dbActive) {
            
          }
        }
      }

      return customerInfo;
    } catch (error) {
      this.logError('Error refreshing subscription', error);
      throw error;
    }
  }

  // Helper function to normalize strings for comparison by removing invisible characters
  private normalizeString(str: string): string {
    if (!str) return '';
    
    // Trim and remove all invisible characters:
    // - Zero-width spaces (\u200B, \u200C, \u200D)
    // - Zero-width non-breaking space (\uFEFF)
    // - Non-breaking space (\u00A0)
    // - Line separator (\u2028)
    // - Paragraph separator (\u2029)
    // - Various space characters
    return str.trim()
      .replace(/[\u200B-\u200F\uFEFF\u00A0\u2000-\u200A\u2028\u2029\u202F\u205F\u3000]/g, '');
  }

  // Log character codes and names of a string for debugging
  private logCharCodes(label: string, str: string): void {
    if (!str) {
      
      return;
    }
    
    
    for (let i = 0; i < str.length; i++) {
      const char = str[i];
      const code = str.charCodeAt(i);
      const hexCode = code.toString(16).padStart(4, '0');
      let charName = '';
      
      // Add descriptive names for common invisible characters
      if (code === 0x200B) charName = 'ZERO WIDTH SPACE';
      else if (code === 0x200C) charName = 'ZERO WIDTH NON-JOINER';
      else if (code === 0x200D) charName = 'ZERO WIDTH JOINER';
      else if (code === 0xFEFF) charName = 'ZERO WIDTH NO-BREAK SPACE';
      else if (code === 0x00A0) charName = 'NON-BREAKING SPACE';
      else if (code === 0x2028) charName = 'LINE SEPARATOR';
      else if (code === 0x2029) charName = 'PARAGRAPH SEPARATOR';
      else if (code >= 0x2000 && code <= 0x200A) charName = 'SPACE VARIANT';
      else if (code === 0x3000) charName = 'IDEOGRAPHIC SPACE';
      
      
    }
  }

  // Purchase a plan by its product identifier
  async purchasePlan(planName: string) {
    try {
      // Remove standalone initialize() - throwIfNotLoggedIn() will handle initialization with user ID
      
      // Debug logging for planName character codes
      
      
      // Original input for reference
      
      this.logCharCodes('Raw plan characters:', planName);
      
      // Normalize the input
      const normalizedPlanName = this.normalizeString(planName);
      
      this.logCharCodes('Normalized plan characters:', normalizedPlanName);
      
      

      if (!this.isMobilePlatform()) {
        
        return {
          success: false,
          error: 'Purchases are only available on mobile devices'
        };
      }

      // Fail early if not logged in
      try {
        await this.throwIfNotLoggedIn();
      } catch (error) {
        
        return {
          success: false,
          error: 'User not logged in to RevenueCat'
        };
      }

      
      const offerings = await Purchases.getOfferings();

      const currentOffering = offerings.current;
      if (!currentOffering) {
        
        return {
          success: false,
          error: 'Current offering not available'
        };
      }

      // Check if packages are available
      if (!currentOffering.availablePackages || currentOffering.availablePackages.length === 0) {
        
        return {
          success: false,
          error: 'No packages available'
        };
      }

      // Log all available packages with character codes for debugging
      
      currentOffering.availablePackages.forEach((pkg, index) => {
        const pkgId = pkg.identifier;
        
        // Add the requested debug logging format
        
        
        
        
        // Log and normalize each package identifier
        this.logCharCodes(`Package ${index} raw identifier:`, pkgId);
        const normalizedPkgId = this.normalizeString(pkgId);
        
        this.logCharCodes(`Package ${index} normalized identifier:`, normalizedPkgId);
      });

      // Use normalized matching
      let matchedPackage = null;
      
      // First try normalized exact match
      for (const pkg of currentOffering.availablePackages) {
        const normalizedPkgId = this.normalizeString(pkg.identifier);
        
        // Log comparison for each package
        
        
        
        
        if (normalizedPlanName === normalizedPkgId) {
          matchedPackage = pkg;
          
          break;
        }
      }

      // If no match found, try a "contains" match as fallback only if the input is non-empty
      if (!matchedPackage && normalizedPlanName.length > 0) {
        
        
        for (const pkg of currentOffering.availablePackages) {
          const normalizedPkgId = this.normalizeString(pkg.identifier);
          
          // Log comparison for fallback matching
          
          
          if (normalizedPkgId.includes(normalizedPlanName) || normalizedPlanName.includes(normalizedPkgId)) {
            
            matchedPackage = pkg;
            break;
          }
        }
      }

      if (!matchedPackage) {
        const availableIdentifiers = currentOffering.availablePackages.map(p => {
          const normalizedId = this.normalizeString(p.identifier);
          return `"${p.identifier}" (raw length: ${p.identifier.length}, normalized: "${normalizedId}" length: ${normalizedId.length})`;
        }).join(', ');
        
        
        
        return {
          success: false,
          error: `No package found for plan: ${planName}`
        };
      }

      
      
      // Actually purchase the package
      const result = await Purchases.purchasePackage({ aPackage: matchedPackage });
      

      return {
        success: true,
        result
      };

    } catch (error: any) {
      if (error.code === 'PURCHASE_CANCELLED_ERROR') {
        
        return {
          success: false,
          cancelled: true,
          error: 'Purchase cancelled by user'
        };
      }

      
      return {
        success: false,
        error: error.message || 'Unknown purchase error'
      };
    }
  }

  // Explicitly log out from RevenueCat
  // NOTE: We avoid calling Purchases.logOut() as it creates anonymous IDs
  // Instead, we just reset our internal state and let the next login configure with the new user
  async logoutFromRevenueCat(): Promise<boolean> {
    if (!this.isMobilePlatform()) {
      
      return true;
    }

    try {
      
      
      // Log current state before logout
      
      
      // IMPORTANT: According to RevenueCat documentation, calling logOut() creates anonymous IDs
      // To avoid anonymous IDs completely, we only reset our internal state
      // The next user login will re-configure RevenueCat with their user ID
      
      // Wait for any ongoing login process to complete before resetting
      if (this.rcLoginInProgress) {
        
        let waitCount = 0;
        while (this.rcLoginInProgress && waitCount < 10) {
          await new Promise(resolve => setTimeout(resolve, 500));
          waitCount++;
        }
        if (this.rcLoginInProgress) {
          
        }
      }
      
      // Reset all login and initialization state
      this.isRcLoggedIn = false;
      this.rcLoginUserID = null;
      this.rcLoginInProgress = false;
      
      // Reset initialization state so RevenueCat can be re-configured with new user
      this.initialized = false;
      this.initializing = false;
      this.initPromise = null;
      this.initializationError = null;
      
      
      
      
      
      return true;
    } catch (error) {
      
      
      // Force reset state even on error to prevent stuck states
      this.isRcLoggedIn = false;
      this.rcLoginUserID = null;
      this.rcLoginInProgress = false;
      this.initialized = false;
      this.initializing = false;
      this.initPromise = null;
      this.initializationError = null;
      
      return false;
    }
  }

  // Force logout and re-login to fix anonymous ID issues
  async forceLoginFix(): Promise<boolean> {
    if (!this.isMobilePlatform()) {
      
      return false;
    }

    try {
      
      
      // Get current Supabase user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError || !user) {
        
        return false;
      }

      
      
      // Reset RevenueCat state completely to force re-configuration
      
      this.isRcLoggedIn = false;
      this.rcLoginUserID = null;
      this.initialized = false;
      this.initializing = false;
      this.initPromise = null;
      this.initializationError = null;
      
      // Wait a moment for state to settle
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Now try to configure RevenueCat fresh with the Supabase user ID
      
      const loginResult = await this.ensureRcLogin();
      
      if (loginResult) {
        
      } else {
        
      }
      
      return loginResult;
    } catch (error) {
      
      this.isRcLoggedIn = false;
      this.rcLoginUserID = null;
      this.initialized = false;
      this.initializing = false;
      this.initPromise = null;
      this.initializationError = null;
      return false;
    }
  }
}

// Create and export the singleton instance
export default PurchaseService.getInstance();

// Convenience functions to access singleton instance methods
// Note: These are NOT React hooks despite the naming pattern - they're just utility functions
export const getRevenueCatAuth = () => {
  const ensureRcLogin = async () => {
    return await PurchaseService.getInstance().ensureRcLogin();
  };

  return { ensureRcLogin };
};

// Convenience function for RevenueCat logout
export const getRevenueCatLogout = () => {
  const logoutFromRevenueCat = async () => {
    return await PurchaseService.getInstance().logoutFromRevenueCat();
  };

  return { logoutFromRevenueCat };
};
